<template>
  <div>
    <!-- Filters -->
    <v-card flat class="mb-4">
      <v-row dense align="center">
        <v-col cols="12" sm="3">
          <v-autocomplete
            v-model="selectedUserId"
            :items="users"
            item-text="full_name"
            item-value="id"
            label="Select User"
            clearable
            dense
            outlined
            @change="onUserChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.branch ? item.branch.branch_name : "" }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="2">
          <v-select
            v-model="period"
            :items="periodOptions"
            label="Period"
            dense
            outlined
            @change="onPeriodChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <v-menu
            v-if="period !== 'year'"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateDisplay"
                :label="dateLabel"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-if="period === 'day' || period === 'week'"
              v-model="selectedDate"
              @input="onDatePicked"
            ></v-date-picker>
            <v-date-picker
              v-else-if="period === 'month'"
              v-model="selectedMonth"
              type="month"
              @input="onMonthPicked"
            ></v-date-picker>
          </v-menu>
          <v-select
            v-else
            v-model="selectedYear"
            :items="yearOptions"
            label="Select Year"
            dense
            outlined
            @change="onYearChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="1" class="d-flex align-center">
          <span class="caption grey--text text-no-wrap">{{
            dateRangeDisplay
          }}</span>
        </v-col>
        <v-col cols="12" sm="2">
          <v-select
            v-model="statusFilter"
            :items="statusOptions"
            label="Status"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <v-select
            v-model="categoryFilter"
            :items="categoryOptions"
            label="Category"
            clearable
            dense
            outlined
          ></v-select>
        </v-col>
        <v-col cols="12" sm="1" class="d-flex align-center">
          <v-btn
            icon
            color="primary"
            @click="loadHistory"
            :disabled="!selectedUserId"
          >
            <v-icon>mdi-filter</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- User Progress Card -->
    <task-user-progress-card
      v-if="selectedUserId"
      :progress="user_progress"
      class="mb-4"
    />

    <!-- History Table -->
    <v-data-table
      v-if="selectedUserId"
      :items="user_history_logs"
      :headers="headers"
      :search="search"
      :items-per-page="15"
      sort-by="log_date"
      sort-desc
      show-expand
      single-expand
    >
      <template v-slot:top>
        <v-text-field
          v-model="search"
          label="Search..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          class="mb-2"
        ></v-text-field>
      </template>

      <template v-slot:item.log_date="{ item }">
        {{ item.log_date ? item.log_date.substr(0, 10) : "" }}
      </template>

      <template v-slot:item.title="{ item }">
        {{ item.task ? item.task.title : "" }}
      </template>

      <template v-slot:item.category="{ item }">
        {{ item.task ? item.task.category || "-" : "-" }}
      </template>

      <template v-slot:item.priority="{ item }">
        <v-chip
          :color="priorityColor(item.task ? item.task.priority : 'basic')"
          small
          dark
        >
          {{ priorityLabel(item.task ? item.task.priority : "basic") }}
        </v-chip>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip :color="statusColor(item.status)" small dark>
          {{ statusLabel(item.status) }}
        </v-chip>
        <v-tooltip bottom v-if="item.remarks && item.remarks.length > 0">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small color="orange" class="ml-1"
              >mdi-comment-text-multiple</v-icon
            >
          </template>
          <span>{{ item.remarks.length }} remark(s)</span>
        </v-tooltip>
      </template>

      <template v-slot:item.completed_at="{ item }">
        {{ item.completed_at ? formatDate(item.completed_at) : "-" }}
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-4">
          <v-row dense>
            <v-col cols="3">
              <div class="caption grey--text">Task Description:</div>
              <div>
                {{
                  item.task ? item.task.description || "No description" : "-"
                }}
              </div>
            </v-col>
            <v-col cols="3">
              <div class="caption grey--text">Completion Note:</div>
              <div>{{ item.completion_note || "No note provided" }}</div>
            </v-col>
            <v-col cols="3">
              <div class="caption grey--text">Admin Comment:</div>
              <div>{{ item.admin_comment || "No comment" }}</div>
              <div v-if="item.acknowledged_by_user" class="caption mt-1">
                - {{ item.acknowledged_by_user.full_name }}
              </div>
            </v-col>
            <v-col cols="3" v-if="item.remarks && item.remarks.length > 0">
              <div class="caption grey--text">Remarks:</div>
              <div v-for="(r, i) in item.remarks" :key="i" class="mb-1">
                <v-chip
                  x-small
                  :color="r.status_set === 'ongoing' ? 'orange' : 'grey'"
                  dark
                  class="mr-1"
                >
                  {{ r.status_set === "ongoing" ? "Ongoing" : "Pending" }}
                </v-chip>
                <span class="body-2">{{ r.remark }}</span>
                <div class="caption grey--text">
                  {{ r.user ? r.user.full_name : "" }} &middot;
                  {{
                    r.created_at ? new Date(r.created_at).toLocaleString() : ""
                  }}
                </div>
              </div>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>

    <v-card v-else flat class="text-center pa-6 grey--text">
      Select a user to view their task history.
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TaskUserProgressCard from "./TaskUserProgressCard.vue";

export default {
  components: { TaskUserProgressCard },
  data() {
    const now = new Date();
    const today = now.toISOString().substr(0, 10);

    return {
      selectedUserId: null,
      period: "month",
      selectedDate: today,
      selectedMonth: today.substr(0, 7),
      selectedYear: today.substr(0, 4),
      dateFrom: null,
      dateTo: null,
      dateMenu: false,
      statusFilter: null,
      categoryFilter: null,
      search: "",
      periodOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
        { text: "Year", value: "year" },
      ],
      statusOptions: [
        { text: "Pending", value: "pending" },
        { text: "Ongoing", value: "ongoing" },
        { text: "Completed", value: "completed" },
        { text: "Acknowledged", value: "acknowledged" },
        { text: "Missed", value: "missed" },
      ],
      categoryOptions: [
        { text: "Operations", value: "operations" },
        { text: "Maintenance", value: "maintenance" },
        { text: "Admin", value: "admin" },
        { text: "Sales", value: "sales" },
        { text: "Custom", value: "custom" },
      ],
      headers: [
        { text: "Date", value: "log_date", width: "110" },
        { text: "Task", value: "title" },
        { text: "Category", value: "category", width: "120" },
        { text: "Priority", value: "priority", width: "110" },
        { text: "Status", value: "status", width: "120" },
        { text: "Completed", value: "completed_at", width: "160" },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      users: "auth/users",
      user_progress: "reminder/user_progress",
      user_history_logs: "reminder/user_history_logs",
    }),
    dateLabel() {
      const map = {
        day: "Select Date",
        week: "Select Week",
        month: "Select Month",
        year: "Select Year",
      };
      return map[this.period] || "Select Date";
    },
    dateDisplay() {
      if (this.period === "month") return this.selectedMonth;
      if (this.period === "year") return this.selectedYear;
      return this.selectedDate;
    },
    dateRangeDisplay() {
      if (!this.dateFrom || !this.dateTo) return "";
      if (this.dateFrom === this.dateTo) return this.dateFrom;
      return this.dateFrom + " ~ " + this.dateTo;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= currentYear - 5; y--) {
        years.push({ text: String(y), value: String(y) });
      }
      return years;
    },
  },
  created() {
    if (!this.users || this.users.length === 0) {
      this.get_users();
    }
    this.calculateDateRange();
  },
  methods: {
    ...mapActions({
      get_users: "auth/get_users",
      get_user_progress: "reminder/get_user_progress",
      get_user_history_logs: "reminder/get_user_history_logs",
    }),
    onPeriodChange() {
      this.calculateDateRange();
    },
    onDatePicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    onYearChange() {
      this.calculateDateRange();
    },
    onMonthPicked() {
      this.dateMenu = false;
      this.calculateDateRange();
    },
    calculateDateRange() {
      if (this.period === "day") {
        this.dateFrom = this.selectedDate;
        this.dateTo = this.selectedDate;
      } else if (this.period === "week") {
        const d = new Date(this.selectedDate + "T00:00:00");
        const day = d.getDay();
        const diffToMon = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMon);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        this.dateFrom = monday.toISOString().substr(0, 10);
        this.dateTo = sunday.toISOString().substr(0, 10);
      } else if (this.period === "month") {
        const parts = this.selectedMonth.split("-");
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        this.dateFrom = firstDay.toISOString().substr(0, 10);
        this.dateTo = lastDay.toISOString().substr(0, 10);
      } else if (this.period === "year") {
        const year = this.selectedYear || this.selectedDate.substr(0, 4);
        this.dateFrom = year + "-01-01";
        this.dateTo = year + "-12-31";
      }
    },
    onUserChange() {
      if (this.selectedUserId) {
        this.get_user_progress(this.selectedUserId);
        this.loadHistory();
      }
    },
    loadHistory() {
      if (!this.selectedUserId) return;
      const filters = { user_id: this.selectedUserId };
      if (this.dateFrom) filters.date_from = this.dateFrom;
      if (this.dateTo) filters.date_to = this.dateTo;
      if (this.statusFilter) filters.status = this.statusFilter;
      if (this.categoryFilter) filters.category = this.categoryFilter;
      this.get_user_history_logs(filters);
    },
    formatDate(d) {
      if (!d) return "";
      return new Date(d).toLocaleString();
    },
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    priorityLabel(p) {
      return p === "very_urgent"
        ? "Very Urgent"
        : p === "urgent"
        ? "Urgent"
        : "Basic";
    },
    statusColor(s) {
      const map = {
        pending: "grey",
        ongoing: "orange",
        completed: "blue",
        acknowledged: "green",
        missed: "red",
      };
      return map[s] || "grey";
    },
    statusLabel(s) {
      const map = {
        pending: "Pending",
        ongoing: "Ongoing",
        completed: "Submitted",
        acknowledged: "Acknowledged",
        missed: "Missed",
      };
      return map[s] || s;
    },
  },
};
</script>
